import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Send, CheckCircle2, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

export default function AIVisibilityDigestPreview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState("");
  const [ctx, setCtx]   = useState(null);
  const [err, setErr]   = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Weekly Digest Preview | WebsiteTech.pro";
    if (user === undefined) return;
    if (!user) { setLoading(false); return; }
    (async () => {
      try {
        const [{ data }, { data: s }] = await Promise.all([
          axios.post(`${API}/ai-visibility/digest/preview`, {},
                      { headers: httpAuth() }),
          axios.get(`${API}/ai-visibility/digest/settings`, { headers: httpAuth() }),
        ]);
        setHtml(data.html); setCtx(data.context);
        setEnabled(!!s.enabled);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not build the preview");
      } finally { setLoading(false); }
    })();
  }, [user]);

  const subscribe = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/ai-visibility/digest/settings`,
        { enabled: true }, { headers: httpAuth() });
      setEnabled(true);
      toast.success("You're in — first digest ships Monday 08:00 UTC");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ai-visibility/digest/send-now`,
        {}, { headers: httpAuth() });
      toast.success(data.sent ? "Preview emailed to you"
                                : "Preview prepared (email service off)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
    </div>
  );
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Sign in to preview</h1>
          <Link to="/login?redirect=/ai-visibility-monitor/digest-preview"
                className="mt-4 inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-bold">
            Sign in
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="aivis-digest-preview-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/ai-visibility-monitor")}
                data-testid="digest-preview-back"
                className="text-sm text-slate-500 hover:text-slate-900 mb-3 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to AI Visibility
        </button>
        <div className="text-[11px] uppercase tracking-widest text-fuchsia-600 font-bold">
          Weekly digest · Full-page preview
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1"
            data-testid="digest-preview-title">
          Here's exactly what lands in your inbox
        </h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          A real preview built from your most recent AI Visibility runs. Subscribe once and this ships every Monday at 08:00 UTC.
        </p>

        {err && (
          <div className="mt-6 rounded-xl bg-amber-50 ring-1 ring-amber-200 p-4 text-sm text-amber-800"
               data-testid="digest-preview-error">
            {err}. <Link to="/ai-visibility-monitor" className="font-bold underline">Run a simulation</Link> first, then come back.
          </div>
        )}

        {html && (
          <>
            {ctx && (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center"
                   data-testid="digest-preview-summary">
                <Tile label="Overall" value={`${ctx.score}/100`} />
                <Tile label="Δ vs last week"
                       value={ctx.prev_score == null ? "—"
                                : `${ctx.score_delta >= 0 ? "+" : ""}${ctx.score_delta}`} />
                <Tile label="Models" value={ctx.models.length} />
                <Tile label="Missed prompts" value={(ctx.missed_prompts||[]).length} />
              </div>
            )}
            <div className="mt-4 rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-white shadow-sm"
                 data-testid="digest-preview-render">
              <div className="bg-slate-100 px-4 py-2 text-xs text-slate-500 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Inbox preview · From: WebsiteTech
              </div>
              <div className="p-6" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {enabled ? (
                <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 ring-1 ring-emerald-200 text-emerald-700 px-4 py-2 text-sm font-bold"
                       data-testid="digest-preview-already-subscribed">
                  <CheckCircle2 className="w-4 h-4" /> You're subscribed
                </span>
              ) : (
                <button onClick={subscribe} disabled={busy}
                        data-testid="digest-preview-subscribe"
                        className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Subscribe · every Monday
                </button>
              )}
              <button onClick={sendNow} disabled={busy}
                      data-testid="digest-preview-send-now"
                      className="rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-800 px-4 py-2 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50">
                <Send className="w-4 h-4" /> Email me this preview
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-lg bg-white ring-1 ring-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
